// API Service Layer - Centralized API management
import axios from 'axios';

/**
 * Get BASE_URL with automatic environment detection (same logic as apiClient.js)
 * Priority:
 * 1. Auto-detect from window.location.hostname (dynamic)
 * 2. Environment variable fallback
 * 3. Default to localhost for development
 */
const getBaseUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol; // 'http:' or 'https:'
  
  // Local development detection
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'http://localhost:8080';
  } else {
    // Production: Use same protocol and hostname as frontend
    return `${protocol}//${hostname}`;
  }
};

const BASE_URL = getBaseUrl();

// Base API configuration for auth endpoints
// Gateway URL: {BASE_URL}/api/auth (routes to login service)
// Example: http://localhost:8080/api/auth or https://helpdesk-pro.dhanushinfotech.com/api/auth
const API = axios.create({
  baseURL: `${BASE_URL}/api/auth`,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Ticket API configuration (uses /gateway/api/ticket, not /api/auth)
// Gateway URL: {BASE_URL}/gateway/api/ticket
// Example: http://localhost:8080/gateway/api/ticket or https://helpdesk-pro.dhanushinfotech.com/gateway/api/ticket
const TICKET_API = axios.create({
  baseURL: BASE_URL,
  timeout: parseInt(process.env.REACT_APP_API_TIMEOUT) || 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication (for both API instances)
const addAuthInterceptor = (apiInstance) => {
  apiInstance.interceptors.request.use(
    (config) => {
      // Use sessionStorage only
      // Support both 'token' and 'authToken' for compatibility
      const token = sessionStorage.getItem('token') || sessionStorage.getItem('authToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        
        // Extract user info from token for headers (if needed)
        // Backend filter extracts from JWT, but we can add headers as backup
        try {
          const { extractUserInfo } = require('../utils/jwtDecoder');
          const userInfo = extractUserInfo(token);
          if (userInfo) {
            // Only add if not in JWT (backend filter will extract from JWT)
            // These are optional - backend filter handles JWT extraction
            if (userInfo.userId) {
              config.headers['X-User-Id'] = userInfo.userId.toString();
            }
            if (userInfo.organizationId || userInfo.tenantId) {
              config.headers['X-Organization-Id'] = (userInfo.organizationId || userInfo.tenantId).toString();
            }
            if (userInfo.roleId) {
              config.headers['X-User-Role'] = userInfo.roleId.toString();
            }
          }
        } catch (error) {
          // JWT decoding failed, continue without headers
          console.warn('Failed to extract user info for headers:', error);
        }
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for error handling
  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        // Check if this is a login request - don't redirect for login failures
        const requestUrl = error.config?.url || '';
        const isLoginRequest = requestUrl.includes('/login') || 
                               requestUrl.includes('/userLogin/login') ||
                               requestUrl.includes('/signin') ||
                               requestUrl.includes('/sign-in');
        
        if (!isLoginRequest) {
          import('../services/logoutService').then(({ performUnauthorizedLogout, isLogoutInProgress }) => {
            if (!isLogoutInProgress()) {
              //console.log('🔄 401 Unauthorized - centralized logout');
              performUnauthorizedLogout('401');
            }
          });
          return Promise.reject(error);
        }
      }
      return Promise.reject(error);
    }
  );
};

// Apply interceptors to both API instances
addAuthInterceptor(API);
addAuthInterceptor(TICKET_API);

// Request interceptor - Log requests in development
if (process.env.NODE_ENV === 'development') {
  const addLoggingInterceptor = (apiInstance, name) => {
    apiInstance.interceptors.request.use(
      (config) => {
        //console.log(`[${name} Request] ${config.method.toUpperCase()} ${config.url}`, config.data);
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor - Log responses in development
    apiInstance.interceptors.response.use(
      (response) => {
        //console.log(`[${name} Response] ${response.config.url}`, response.data);
        return response;
      },
      (error) => {
        console.error(`[${name} Error] ${error.config?.url}`, error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  };

  addLoggingInterceptor(API, 'API');
  addLoggingInterceptor(TICKET_API, 'TICKET_API');
}

export default API;
export { TICKET_API };
